import React, { useState } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { Alert, Button } from "@narmi/design_system";
import { ContentCard } from "cerulean";

import styles from "./SavvyMoneyWidget.module.scss";
import BorderCard from "../../BorderCard";

interface SavvyMoneyProps {
  hasErrored: boolean;
  isLoading: boolean;
  refetch: () => void;
  borderCard?: boolean;
}

const ErrorContent = ({
  isLoading,
  hasAlreadyRefetched,
  onRefetch,
}: {
  isLoading: boolean;
  hasAlreadyRefetched: boolean;
  onRefetch: () => void;
}) => {
  const { l10n } = useLocalization();
  const errorMessage = l10n.getString("savvy-money-widget-error");
  const secondaryErrorMessage = l10n.getString(
    "savvy-money-widget-secondary-error"
  );
  const showSecondaryMessage = hasAlreadyRefetched && !isLoading;

  return (
    <div className={styles.errorContainer}>
      <Alert isActive isDismissable={false} kind="warn">
        <div>{errorMessage}</div>
        <div>
          {showSecondaryMessage ? (
            secondaryErrorMessage
          ) : (
            <Button
              testId="savymoney-error-retry"
              disabled={isLoading}
              onClick={onRefetch}
              kind="plain"
            >
              Try again
            </Button>
          )}
        </div>
      </Alert>
    </div>
  );
};

const IframeContent = ({ url }: { url: string }) => (
  <div className={styles.iframeContainer}>
    <iframe
      title="SavvyMoney"
      src={`${url}&linkToAction=${encodeURIComponent(
        `${window.location.origin}/credit_monitor`
      )}`}
      style={{ width: "338px", height: "350px", border: "none" }}
    />
  </div>
);

const SavvyMoneyWidget: React.FC<SavvyMoneyProps> = ({
  hasErrored,
  isLoading,
  refetch,
  borderCard = false,
}) => {
  const [hasAlreadyRefetched, setHasAlreadyRefetched] = useState(false);
  const { l10n } = useLocalization();

  const url = entities.savvyMoney.useSavvyMoneyUrl(
    entities.savvyMoney.SavvyMoneyViewUrl.WebWidget
  );

  const handleRefetch = () => {
    if (!hasAlreadyRefetched) {
      setHasAlreadyRefetched(true);
      refetch();
    }
  };

  if (!url && !hasErrored) {
    return null;
  }

  const content = hasErrored ? (
    <ErrorContent
      isLoading={isLoading}
      hasAlreadyRefetched={hasAlreadyRefetched}
      onRefetch={handleRefetch}
    />
  ) : (
    url && <IframeContent url={url} />
  );

  if (borderCard) {
    return (
      <BorderCard
        title={l10n.getString(
          "dashboard-customization-section-credit-monitoring"
        )}
      >
        {content}
      </BorderCard>
    );
  }

  return <ContentCard kind="elevated">{content}</ContentCard>;
};

export default SavvyMoneyWidget;
